import {Alert, Button} from 'antd';
import React, {FC, useCallback, useEffect, useRef, useState} from 'react';

/** Executes a request to server and hashes the response. */
const fetchHash = async (path = '/'): Promise<string> => {
  let html = '';
  try {
    const res = await fetch(path, {cache: 'no-store'});
    html = await res.text();
  } catch (err) {
    console.error('AutoUpdater: failed to check for version update', err);
    return '';
  }

  const encodedHtml = new TextEncoder().encode(html);
  const hash = await window.crypto.subtle.digest('SHA-1', encodedHtml);

  const hashArray = Array.from(new Uint8Array(hash));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return hashHex;
};

const CACHE_KEY = 'AutoUpdater-Hash';
const CACHE_EXPIRY_KEY = 'AutoUpdater-Expiration';

/** Retrieves the hash (using localStorage cache) */
const getHash = async (
  path: string,
  options: {cacheMs: number},
): Promise<string> => {
  const now = Date.now();
  const expiry = Number(localStorage.getItem(CACHE_EXPIRY_KEY) ?? 0);

  if (expiry > now) {
    console.debug(`AutoUpdater: getHash: using cache in localStorage`);
    return localStorage.getItem(CACHE_KEY) ?? '';
  }
  console.debug(`AutoUpdater: getHash: *not* using cache`);

  const newHash = await fetchHash(path);

  localStorage.setItem(CACHE_KEY, newHash);
  localStorage.setItem(CACHE_EXPIRY_KEY, String(now + options.cacheMs));

  return newHash;
};

/** Checks if new build is available from the server. Displays a notification
 * to the user and offers them the option to reload their page using a button.
 */
export const AutoUpdater: FC<{
  /** The path that will be used to check for new versions. Defaults to "/",
   * which will point to the "index.html" file. It is also possible
   * to use "/asset-manifest.json" when using create-react-app. */
  path?: string;
  /** If set, will check periodically if a new version is available.
   * This value should be higher than (or equal) "cacheMs".
   * Warning: to make sure we don't send too many requests to the server,
   * please avoid setting this value too low (<15min).
   */
  intervalMs?: number;
  /** Time (in milliseconds) during which the hash value from server will
   * be cached. This is used to reduce requests:
   * 1. To avoid doing a request during app mount when user refreshes app (or opens new windows)
   * 2. If user has multiple windows opened
   * Defaults to 15min.
   */
  cacheMs?: number;
  /** To disable the version checking completely. */
  disabled?: boolean;
}> = (props) => {
  const {path = '/', cacheMs = 15 * 60 * 1000, intervalMs, disabled} = props;

  const [newVersion, setNewVersion] = useState(false);
  const currentHashRef = useRef<string | null>(null);

  const check = useCallback(async () => {
    if (disabled) {
      return;
    }

    // if there's already a new version available, there is no need to check again
    if (newVersion) {
      console.debug(
        `AutoUpdater: new version is *already* available, skip check`,
      );
      return;
    }

    console.debug(`AutoUpdater: checking if new version is available`);

    const hash = await getHash(path, {cacheMs});

    if (!currentHashRef.current) {
      console.debug(`AutoUpdater: first mount, hash set`);

      currentHashRef.current = hash;
      return;
    }

    if (hash !== currentHashRef.current) {
      console.debug(`AutoUpdater: new version is available`);
      setNewVersion(true);
    }
  }, [path, newVersion, cacheMs, disabled]);

  useEffect(() => {
    void check();

    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', onVisible);

    // if another window/tab changed the hash (ie another tab was updated),
    // consider that a new version is available:
    const onStorage = (ev: StorageEvent) => {
      if (ev.key === CACHE_KEY) {
        if (ev.newValue !== currentHashRef.current) {
          setNewVersion(true);
        }
      }
    };
    window.addEventListener('storage', onStorage);

    let intervalId: number | undefined = undefined;
    if (intervalMs) {
      intervalId = window.setInterval(check, intervalMs);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }

      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('storage', onStorage);
    };
  }, [intervalMs, check]);

  if (!newVersion) {
    return null;
  }

  return (
    <Alert
      type="info"
      banner
      message={
        <div>
          Une nouvelle version du logiciel est disponible. Merci de terminer
          votre saisie en cours et{' '}
          <Button
            type="link"
            onClick={() => window.location.reload()}
            style={{padding: 'inherit', height: 'inherit'}}
          >
            cliquez ici pour l’installer.
          </Button>
        </div>
      }
    />
  );
};
